import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Chip, Stack } from '@mui/material';
import {
  PlayArrow as PlayArrowIcon,
  School as SchoolIcon,
  AccessTime as AccessTimeIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import Button from './UIKit/Button';

const getDifficultyColor = (level) => {
  switch (level?.toLowerCase()) {
    case 'beginner': case 'начинающий': return 'success';
    case 'intermediate': case 'средний': return 'warning';
    case 'advanced': case 'продвинутый': return 'error';
    default: return 'default';
  }
};

const formatTime = (minutes, t) => {
  if (!minutes || minutes <= 0) return null;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours > 0 && mins > 0) return `${hours} ${t('common:time.shortHour')} ${mins} ${t('common:time.shortMin')}`;
  if (hours > 0) return `${hours} ${t('common:time.shortHour')}`;
  return `${mins} ${t('common:time.shortMin')}`;
};

const MandatoryPathCard = ({ item, index, activeLearningPathId }) => {
  const { t } = useTranslation(['common', 'learningPaths', 'profile']);
  const navigate = useNavigate();

  if (!item?._id) return null;

  const { _id, title, description, image, difficulty, coursesCount, estimatedTimeMinutes, isCompleted } = item;
  const isActive = activeLearningPathId && _id?.toString() === activeLearningPathId?.toString();
  const imageUrl = image || '/images/default-path.jpg';
  const timeLabel = formatTime(estimatedTimeMinutes, t);

  return (
    <Box
      sx={{
        position: 'relative',
        borderRadius: '16px',
        overflow: 'hidden',
        cursor: 'pointer',
        minHeight: '240px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-end',
        opacity: isCompleted ? 0.75 : 1,
        transition: 'opacity 0.2s, transform 0.2s',
        '&:hover': { transform: 'translateY(-2px)' },
      }}
      onClick={() => navigate(`/learning-paths/${_id}`)}
    >
      {/* Background image */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          backgroundImage: `url(${imageUrl})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          zIndex: 0,
        }}
      />

      {/* Gradient overlay */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background: 'linear-gradient(to top, rgba(0,0,0,0.88) 0%, rgba(0,0,0,0.45) 55%, rgba(0,0,0,0.15) 100%)',
          zIndex: 1,
        }}
      />

      {/* Order number — top left */}
      <Box
        sx={{
          position: 'absolute',
          top: 12,
          left: 14,
          zIndex: 2,
          width: 28,
          height: 28,
          borderRadius: '50%',
          backgroundColor: 'rgba(255,255,255,0.22)',
          backdropFilter: 'blur(4px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography variant="caption" sx={{ color: '#fff', fontWeight: 700, lineHeight: 1 }}>
          {index + 1}
        </Typography>
      </Box>

      {/* Difficulty chip — top right */}
      {difficulty && (
        <Box sx={{ position: 'absolute', top: 12, right: 12, zIndex: 2 }}>
          <Chip
            label={t(`common:difficulty.${difficulty}`)}
            color={getDifficultyColor(difficulty)}
            size="small"
            sx={{ fontWeight: 600 }}
          />
        </Box>
      )}

      {/* Active path badge */}
      {isActive && !isCompleted && (
        <Box sx={{ position: 'absolute', top: 12, left: '50%', transform: 'translateX(-50%)', zIndex: 2 }}>
          <Chip
            label={t('learningPaths:activePath')}
            color="primary"
            size="small"
            sx={{ fontWeight: 700 }}
          />
        </Box>
      )}

      {/* Completed overlay badge */}
      {isCompleted && (
        <Box
          sx={{
            position: 'absolute',
            top: 12,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 2,
          }}
        >
          <Chip
            icon={<CheckCircleIcon sx={{ fontSize: '1rem !important' }} />}
            label={t('profile:completed')}
            color="success"
            size="small"
            sx={{ fontWeight: 700 }}
          />
        </Box>
      )}

      {/* Content */}
      <Box sx={{ position: 'relative', zIndex: 2, p: { xs: 2, sm: 2.5 } }}>
        <Typography
          variant="h6"
          sx={{
            color: '#fff',
            fontWeight: 700,
            mb: 0.5,
            lineHeight: 1.3,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {title}
        </Typography>

        {description && (
          <Typography
            variant="body2"
            sx={{
              color: 'rgba(255,255,255,0.72)',
              mb: 1.5,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {description}
          </Typography>
        )}

        {/* Stats row */}
        <Stack direction="row" spacing={2} mb={1.5} alignItems="center">
          {coursesCount > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <SchoolIcon sx={{ fontSize: '1rem', color: 'rgba(255,255,255,0.7)' }} />
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                {coursesCount} {t('common:stats.courses')}
              </Typography>
            </Box>
          )}
          {timeLabel && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <AccessTimeIcon sx={{ fontSize: '1rem', color: 'rgba(255,255,255,0.7)' }} />
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                {timeLabel}
              </Typography>
            </Box>
          )}
        </Stack>

        <Button
          variant="contained"
          color={isCompleted ? 'success' : 'primary'}
          startIcon={isCompleted ? <CheckCircleIcon /> : <PlayArrowIcon />}
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/learning-paths/${_id}`);
          }}
          size="small"
        >
          {isCompleted
            ? t('profile:completed')
            : isActive
            ? t('profile:continue_learning')
            : t('learningPaths:startLearning')}
        </Button>
      </Box>
    </Box>
  );
};

export default MandatoryPathCard;
